import { fieldConfig } from "@autoform/zod";
import { z } from "zod";

import { SocialLinkSchema } from "@/components/forms/schemas/social-link";
import {
  seoGeoFields,
  systemFields,
  zGenreRef,
  zReasoning,
  zTransliterations,
} from "@/lib/zod/show-fragments";

const FaqItemSchema = z.object({
  question: z.string(),
  answer: z.string(),
});

/**
 * SUBMIT schema — what `PUT /artists/enrich` accepts.
 *
 * `.passthrough()` is load-bearing, not tidiness. Approve & Save PUTs this
 * object back and the endpoint **full-replaces** the stored enrichment, so any
 * key Zod strips here is destroyed permanently. `taskName` (the Tina job
 * pointer the entity trace is rendered from) and `enrichmentSources` are
 * pipeline-owned and were being wiped on every single approval. Enumerating
 * them would just re-break on the next key the enrichment flow adds — the
 * event schema learned this in a3ea7ae and artists were missed.
 */
const artistEnrichmentSubmitSchema = z
  .object({
    id: z.string(),
    reasoning: z
      .object({
        text: z.string().nullish(),
        confidence: z.number().min(0).max(1).nullish(),
      })
      .nullish(),
    about: z.string().superRefine(
      fieldConfig({
        fieldType: "textarea",
      }),
    ),
    // SEO/GEO contract (shared across entities).
    normalizedTitle: z.string().nullish(),
    // Full <title>. Null means "use the app's title formula".
    seoTitle: z.string().nullish(),
    shortDescription: z
      .string()
      .nullish()
      .superRefine(fieldConfig({ fieldType: "textarea" })),
    keywords: z.array(z.string()).nullish(),
    relatedSearches: z.array(z.string()).nullish(),
    faq: z.array(FaqItemSchema).nullish(),
    features: z
      .object({
        energy: z.coerce.number().min(0).max(1).nullish(),
        polish: z.coerce.number().min(0).max(1).nullish(),
        queerness: z.coerce.number().min(0).max(1).nullish(),
        nostalgia: z.coerce.number().min(0).max(1).nullish(),
        locality: z.coerce.number().min(0).max(1).nullish(),
        age: z.coerce.number().min(0).max(1).nullish(),
        fashion: z.coerce.number().min(0).max(1).nullish(),
        commerciality: z.coerce.number().min(0).max(1).nullish(),
      })
      .nullish(),
    genres: z
      .array(
        z.object({
          id: z.string(),
          name: z.string(),
        }),
      )
      .nullish(),
    // gyrnaei genres mono otan den exei lavei genres san input apo spotify
    transliterations: zTransliterations,
    socialLinks: z.array(SocialLinkSchema).nullish(),
    isGroup: z.boolean().nullish().default(false),
    members: z
      .array(
        z.object({
          id: z.string().nullish(),
          name: z.string(),
        }),
      )
      .nullish(),
    updatedAt: z.string().datetime({ offset: true }),
    updatedBy: z.string(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: z.string(),
  })
  .passthrough();

/**
 * READ schema — lenient, for rendering whatever the API returned.
 *
 * Every field nullish and `.passthrough()` throughout, so one unexpected value
 * cannot reject the object. It used to: `artistViewSchema` nests the enrichment
 * and `use-artist-repository`'s `get()` hard-`.parse()`d the result, so a single
 * bad enrichment key blanked the whole artist edit page. Consume through
 * `parseRead()` from `@/lib/zod/read-schema`.
 */
export const artistEnrichmentReadSchema = z
  .object({
    id: z.string().nullish(),
    reasoning: zReasoning.nullish(),
    about: z.string().nullish(),
    ...seoGeoFields,
    features: z
      .object({
        energy: z.number().nullish(),
        polish: z.number().nullish(),
        queerness: z.number().nullish(),
        nostalgia: z.number().nullish(),
        locality: z.number().nullish(),
        age: z.number().nullish(),
        fashion: z.number().nullish(),
        commerciality: z.number().nullish(),
      })
      .passthrough()
      .nullish(),
    genres: z.array(zGenreRef).nullish(),
    transliterations: zTransliterations.nullish(),
    socialLinks: z.array(z.unknown()).nullish(),
    isGroup: z.boolean().nullish(),
    members: z
      .array(
        z
          .object({ id: z.string().nullish(), name: z.string().nullish() })
          .passthrough(),
      )
      .nullish(),
    ...systemFields,
  })
  .passthrough();

export const artistEditableEnrichmentSchema = artistEnrichmentSubmitSchema.omit(
  {
    id: true,
    reasoning: true,
    updatedAt: true,
    updatedBy: true,
    createdAt: true,
    createdBy: true,
  },
);

export type ArtistEnrichmentRead = z.infer<typeof artistEnrichmentReadSchema>;
export type ArtistAIEnrichmentType = z.infer<
  typeof artistEnrichmentSubmitSchema
>;
export type ArtistEditableEnrichmentType = z.infer<
  typeof artistEditableEnrichmentSchema
>;
export type ArtistFeaturesType = z.infer<
  typeof artistEnrichmentSubmitSchema.shape.features
>;
export { artistEnrichmentSubmitSchema };
/** @deprecated Prefer the explicit read/submit pair. Kept so existing importers compile. */
export { artistEnrichmentSubmitSchema as artistAIEnrichmentSchema };
